import { uniqueId } from "lodash";
import AppContext from "PFApp/app_context";
import { useTemplate } from "PFCore/helpers/templates";
import useIsPermittedTo from "PFCore/helpers/use_is_permitted_to";
import { useBookingsFromAvailability } from "PFCore/hooks/queries/bookings/bookings_from_availability/use_bookings_from_availability";
import useBookingCategories from "PFCore/hooks/use_booking_categories";
import { Availability, BookingCategory, Engagement, PermissionRule, Profile } from "PFTypes";
import { useContext, useEffect, useRef, useState } from "react";

import { useBookingActivityContext } from "../../parts/providers/booking_activity_context_provider";
import { useBookingModalContext } from "./booking_modal_context_provider";
import { BookingModalDataType, ModalBooking } from "./use_booking_modal";

export type BookingInitialData = {
  title?: string;
  description?: string;
  profileId?: number;
  activityId?: number;
  availability?: Availability;
  bookings?: BookingModalDataType["bookings"];
  categoryId?: number;
  bookingGroupId?: number;
};

const useBookingInitialData = (initialData: BookingInitialData = {}, enabled: boolean) => {
  const {
    store: { currentProfile }
  } = useContext(AppContext);
  const { activity } = useBookingActivityContext();
  const activityTemplate = useTemplate(activity?.template_id);
  const isActivityEngagement = activityTemplate?.key === "engagement";
  const { fetchProfile } = useBookingModalContext();
  const { getBookingCategory } = useBookingCategories();
  const isPermittedTo = useIsPermittedTo(currentProfile);
  const isBookingResourcer = isPermittedTo(PermissionRule.BookingResourcer);

  const [title, setTitle] = useState<string>(initialData.title || "");
  const [description, setDescription] = useState<string>(initialData.description || "");
  const prefilledUserRef = useRef<Profile | null>(null);
  const [selectedUser, setSelectedUser] = useState<Profile | null>(null);
  const [selectedEngagement, setSelectedEngagement] = useState<Engagement | null>(
    isActivityEngagement && activity ? (activity as unknown as Engagement) : null
  );
  const [selectedBookingCategory, setSelectedBookingCategory] = useState<BookingCategory | null>(
    initialData.categoryId ? getBookingCategory(initialData.categoryId) || null : null
  );
  const [bookingItems, setBookingItems] = useState<(Partial<ModalBooking> & { key: string })[]>(
    initialData.bookings?.map((item) => ({
      ...item,
      key: uniqueId(`0+booking_modal_item+`)
    })) ?? []
  );
  const { data: bookingsFromAvailability, isFetching: isBookingsFromAvailabilityLoading } =
    useBookingsFromAvailability({
      profileId: initialData.profileId,
      activityId: initialData.activityId,
      options: {
        enabled: enabled && !!initialData.profileId && !!initialData.activityId
      }
    });

  useEffect(() => {
    if (!enabled || !bookingsFromAvailability) {
      return;
    }
    setTitle(bookingsFromAvailability.title);
    setDescription(bookingsFromAvailability.description);
    setBookingItems(
      bookingsFromAvailability.ranges?.map((item) => ({
        ...item,
        startDate: item.start_date,
        endDate: item.end_date,
        key: uniqueId(`0+booking_modal_item+`)
      })) ?? []
    );
  }, [enabled, bookingsFromAvailability]);

  useEffect(() => {
    const fetchPreFilledProfile = async (profileId) => {
      const profileResponse = await fetchProfile(profileId);
      setSelectedUser(profileResponse);
      prefilledUserRef.current = profileResponse;
    };

    if (!isBookingResourcer && !selectedUser) {
      fetchPreFilledProfile(currentProfile.id);
      return;
    }

    if (!initialData.profileId || !!selectedUser) {
      return;
    }

    fetchPreFilledProfile(initialData.profileId);
  }, [fetchProfile, initialData.profileId, selectedUser, currentProfile.id, isBookingResourcer]);

  return {
    title,
    setTitle,
    description,
    setDescription,
    selectedUser,
    setSelectedUser,
    selectedEngagement,
    setSelectedEngagement,
    prefilledUserRef,
    selectedBookingCategory,
    setSelectedBookingCategory,
    bookingItems,
    setBookingItems,
    isBookingsFromAvailabilityLoading,
    bookingGroupId: initialData.bookingGroupId
  };
};

export default useBookingInitialData;
